/*
Eadin design reminder for this file:
- Minimal, high-contrast light dashboard surface with compact spacing and no decorative gradients.
- System font stack only, sticky table headers, thin separators, hover-only row separation, and row-first planning workflows.
- Upcoming Buys should feel like the OTB table with a compact export section above it, not like a separate card dashboard.
*/

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDown, ChevronUp, Download, Info, Mail } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { arrayHasItems } from "../../helpers/common";
import {
    DemandPlanningLoadingKeys,
    getUpcomingBuysTableData,
    saveForecastDemandProductUpcomingBuyExclude,
} from "../../slices/demandPlanningSlice";
import {
    buildUpcomingBuyMonthOptions,
    buildUpcomingBuySummary,
    buildUpcomingBuySupplierGroups,
    downloadUpcomingBuysCsv,
    downloadUpcomingBuysPdf,
    emailUpcomingBuySupplierDraft,
    formatUpcomingBuyCurrency,
    formatUpcomingBuyDate,
    formatUpcomingBuyMonthLabel,
    formatUpcomingBuyNumber,
    getBuyOverrideTone,
    getUpcomingBuyStatusLabel,
    getUpcomingBuyStatusTone,
    normalizeUpcomingBuyRow,
} from "../../lib/upcomingBuysPlanning";
import { LoaderSpinner } from "../LoaderSpinner";
import { SelectDropdown } from "../SelectDropdown";
import { ConfrimModal } from "../ConfrimModal";
import ProductCatalogBulkFieldMenu from "../productCatalog/ProductCatalogBulkFieldMenu";
import SupplySku from "../SupplySku";
import ViewGovernanceDialog from "./ViewGovernanceDialog";

const SupplierExportMenu = ({ group, selectedMonthLabel }) => {
    return (
        <Menu as="div" className="relative inline-block text-left">
            <MenuButton
                type="button"
                className="inline-flex h-8 items-center rounded-md border border-border bg-white px-3 text-xs font-medium text-primary hover:bg-gray-50"
            >
                <Download className="mr-2 size-4" />
                Export
                <ChevronDown className="ml-2 size-3.5" />
            </MenuButton>
            <MenuItems
                portal
                modal={false}
                anchor={{ to: "bottom end", gap: "0.25rem", padding: "0.5rem" }}
                className="z-[150] w-44 origin-top-right rounded-md border border-border bg-white py-1 shadow-lg outline-none"
            >
                <MenuItem>
                    <button
                        type="button"
                        className="flex w-full items-center px-3 py-2 text-left text-sm text-primary hover:bg-gray-50 focus:outline-none data-[focus]:bg-gray-50"
                        onClick={() =>
                            downloadUpcomingBuysPdf(group.supplier, selectedMonthLabel, group.rows)
                        }
                    >
                        <Download className="mr-2 size-4" />
                        Download PDF
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        type="button"
                        className="flex w-full items-center px-3 py-2 text-left text-sm text-primary hover:bg-gray-50 focus:outline-none data-[focus]:bg-gray-50"
                        onClick={() =>
                            emailUpcomingBuySupplierDraft(
                                group.supplier,
                                selectedMonthLabel,
                                group.rows
                            )
                        }
                    >
                        <Mail className="mr-2 size-4" />
                        Email Supplier
                    </button>
                </MenuItem>
            </MenuItems>
        </Menu>
    );
};

const UpcomingBuysTable = ({
    selectedForecastDemandPlanning,
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
}) => {
    const dispatch = useDispatch();
    const { upcomingBuysTableData, loadingKeys } = useSelector((state) => state.demandPlanning);

    const loading = loadingKeys.includes(DemandPlanningLoadingKeys.getUpcomingBuysTableData);

    const [topSectionCollapsed, setTopSectionCollapsed] = useState(false);
    const [governanceOpen, setGovernanceOpen] = useState(false);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [bulkOverrideConfirmOpen, setBulkOverrideConfirmOpen] = useState(false);
    const [pendingBulkOverride, setPendingBulkOverride] = useState(null);
    const [bulkOverrideSaving, setBulkOverrideSaving] = useState(false);
    const userSelectedMonthRef = useRef(false);
    const lastFilterFetchKeyRef = useRef(null);

    const demandPlanningId = selectedForecastDemandPlanning?.id;
    const demandPlanningYear =
        selectedForecastDemandPlanning?.forecast_year ??
        selectedForecastDemandPlanning?.forecastYear;

    const availableBuyMonths = useMemo(() => {
        const months = upcomingBuysTableData?.availableBuyMonths;
        return arrayHasItems(months) ? months : [];
    }, [upcomingBuysTableData]);

    const monthOptions = useMemo(
        () => buildUpcomingBuyMonthOptions(availableBuyMonths),
        [availableBuyMonths]
    );

    const effectiveBuyMonth =
        selectedMonth ?? upcomingBuysTableData?.activeBuyMonth ?? null;

    const buildUpcomingBuysRequest = useCallback(
        (selectedBuyMonth) => {
            if (!demandPlanningId) return null;

            return {
                demandPlanningId,
                demandPlanningYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
                selectedBuyMonth,
            };
        },
        [
            demandPlanningId,
            demandPlanningYear,
            searchTerm,
            selectedCategoryId,
            selectedSubcategoryId,
            selectedStatusId,
        ]
    );

    const filterFetchKey = useMemo(() => {
        const payload = buildUpcomingBuysRequest(undefined);
        return payload ? JSON.stringify(payload) : null;
    }, [buildUpcomingBuysRequest]);

    useEffect(() => {
        if (!filterFetchKey) return;
        if (lastFilterFetchKeyRef.current === filterFetchKey) return;

        userSelectedMonthRef.current = false;
        setSelectedMonth(null);
        lastFilterFetchKeyRef.current = filterFetchKey;

        dispatch(getUpcomingBuysTableData(buildUpcomingBuysRequest(undefined)));
    }, [dispatch, buildUpcomingBuysRequest, filterFetchKey]);

    useEffect(() => {
        const serverMonth = upcomingBuysTableData?.activeBuyMonth;
        if (!serverMonth || userSelectedMonthRef.current) return;
        setSelectedMonth((current) => (current === serverMonth ? current : serverMonth));
    }, [upcomingBuysTableData?.activeBuyMonth]);

    const handleMonthChange = useCallback(
        (month) => {
            userSelectedMonthRef.current = true;
            setSelectedMonth(month);

            const payload = buildUpcomingBuysRequest(month);
            if (!payload) return;
            dispatch(getUpcomingBuysTableData(payload));
        },
        [buildUpcomingBuysRequest, dispatch]
    );

    const scopedRows = useMemo(() => {
        if (!effectiveBuyMonth) return [];
        const rows = upcomingBuysTableData?.upcomingBuyRows;
        if (!arrayHasItems(rows)) return [];
        return rows.map(normalizeUpcomingBuyRow);
    }, [upcomingBuysTableData, effectiveBuyMonth]);

    const scopedRowIds = useMemo(() => new Set(scopedRows.map((row) => row.id)), [scopedRows]);

    useEffect(() => {
        setSelectedRowIds((current) => current.filter((id) => scopedRowIds.has(id)));
    }, [scopedRowIds]);

    const getRowBuyOverride = (row) => row.buyOverride ?? "include";

    const includedRows = useMemo(
        () => scopedRows.filter((row) => getRowBuyOverride(row) !== "exclude"),
        [scopedRows]
    );

    const excludedRowCount = scopedRows.length - includedRows.length;
    const visibleSelectedRows = useMemo(
        () => scopedRows.filter((row) => selectedRowIds.includes(row.id)),
        [scopedRows, selectedRowIds]
    );
    const allVisibleSelected = scopedRows.length > 0 && visibleSelectedRows.length === scopedRows.length;
    const someVisibleSelected = visibleSelectedRows.length > 0 && !allVisibleSelected;

    const summary = useMemo(() => buildUpcomingBuySummary(includedRows), [includedRows]);

    const supplierGroups = useMemo(
        () => buildUpcomingBuySupplierGroups(includedRows),
        [includedRows]
    );

    const toggleRowSelection = (rowId, checked) => {
        setSelectedRowIds((current) => {
            if (checked) {
                return current.includes(rowId) ? current : [...current, rowId];
            }
            return current.filter((id) => id !== rowId);
        });
    };

    const toggleSelectAllVisible = (checked) => {
        if (checked) {
            setSelectedRowIds(scopedRows.map((row) => row.id));
            return;
        }
        setSelectedRowIds([]);
    };

    const selectedCount = visibleSelectedRows.length;

    const reloadUpcomingBuysTableData = useCallback(() => {
        const payload = buildUpcomingBuysRequest(
            userSelectedMonthRef.current ? effectiveBuyMonth || undefined : undefined
        );
        if (!payload) return Promise.resolve();

        return dispatch(getUpcomingBuysTableData(payload)).unwrap();
    }, [buildUpcomingBuysRequest, dispatch, effectiveBuyMonth]);

    const submitBulkOverride = useCallback(async () => {
        if (!pendingBulkOverride || selectedCount === 0 || !effectiveBuyMonth) return;

        const demandPlanningId = selectedForecastDemandPlanning?.id;
        const demandPlanningYear =
            selectedForecastDemandPlanning?.forecast_year ??
            selectedForecastDemandPlanning?.forecastYear;

        if (demandPlanningId == null || demandPlanningYear == null || demandPlanningYear === "") {
            return;
        }

        setBulkOverrideSaving(true);
        try {
            await dispatch(
                saveForecastDemandProductUpcomingBuyExclude({
                    demandPlanningId,
                    demandPlanningYear,
                    buyMonth: effectiveBuyMonth,
                    forecastDemandProductIds: visibleSelectedRows.map((row) => row.id),
                    excluded: pendingBulkOverride.value === "exclude",
                })
            ).unwrap();

            await reloadUpcomingBuysTableData();

            toast.success(
                `Updated buy override to "${pendingBulkOverride.label}" for ${selectedCount} row${selectedCount === 1 ? "" : "s"}.`,
                { position: "bottom-right" }
            );
            setSelectedRowIds([]);
            setBulkOverrideConfirmOpen(false);
            setPendingBulkOverride(null);
        } catch (error) {
            const message =
                typeof error === "string"
                    ? error
                    : error?.message || "Could not update buy override.";
            toast.error(message, { position: "bottom-right" });
        } finally {
            setBulkOverrideSaving(false);
        }
    }, [
        dispatch,
        pendingBulkOverride,
        reloadUpcomingBuysTableData,
        selectedCount,
        selectedForecastDemandPlanning,
        effectiveBuyMonth,
        visibleSelectedRows,
    ]);

    const selectedMonthLabel = effectiveBuyMonth
        ? formatUpcomingBuyMonthLabel(effectiveBuyMonth)
        : "Select buy month";

    const hasUpcomingBuysPayload =
        upcomingBuysTableData != null &&
        !Array.isArray(upcomingBuysTableData) &&
        typeof upcomingBuysTableData === "object";

    if (loading && !hasUpcomingBuysPayload) {
        return (
            <section className="flex-1 px-4 py-4 lg:px-6">
                <div className="mt-8 flex h-full items-center justify-center">
                    <LoaderSpinner width="80" height="80" border="5" colorSpinner="gray" />
                </div>
            </section>
        );
    }

    return (
        <section className="relative flex-1 py-4">
            {loading ? (
                <div
                    className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/75"
                    aria-live="polite"
                    aria-busy="true"
                >
                    <LoaderSpinner width="64" height="64" border="4" colorSpinner="gray" />
                </div>
            ) : null}
            <div className="mb-4 rounded-lg border border-border bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 border-b border-border pb-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="min-w-0">
                        <div className="mb-2 flex items-center gap-2">
                            <span className="inline-flex w-fit shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                                Upcoming Buys
                            </span>
                        </div>
                        <h2 className="text-xl font-semibold tracking-[-0.02em] text-primary">
                            Monthly buy view
                        </h2>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                        <button
                            type="button"
                            className="inline-flex h-10 items-center justify-center rounded-full border border-border bg-white px-2.5 text-primary hover:bg-gray-50"
                            onClick={() => setGovernanceOpen(true)}
                            aria-label="Open Upcoming Buys governance"
                        >
                            <Info className="size-4" />
                        </button>

                        <div className="min-w-[220px] max-w-[220px]">
                            <SelectDropdown
                                options={monthOptions}
                                value={effectiveBuyMonth || ""}
                                onChange={handleMonthChange}
                                placeholder="Select month"
                                selectClassname="h-10 rounded-md border border-border bg-white px-3 text-sm font-medium shadow-none"
                                disabled={monthOptions.length === 0 || loading}
                            />
                        </div>

                        <button
                            type="button"
                            className="inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={includedRows.length === 0 || loading}
                            onClick={() =>
                                downloadUpcomingBuysCsv(
                                    `eadin-upcoming-buys-${effectiveBuyMonth || "all"}.csv`,
                                    includedRows
                                )
                            }
                        >
                            <Download className="mr-2 size-4" />
                            Export month CSV
                        </button>

                        <button
                            type="button"
                            aria-label={
                                topSectionCollapsed
                                    ? "Expand monthly buy section"
                                    : "Collapse monthly buy section"
                            }
                            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-white p-0 text-primary hover:bg-gray-50"
                            onClick={() => setTopSectionCollapsed((current) => !current)}
                        >
                            {topSectionCollapsed ? (
                                <ChevronDown className="size-4" />
                            ) : (
                                <ChevronUp className="size-4" />
                            )}
                        </button>
                    </div>
                </div>

                {!topSectionCollapsed ? (
                    <div className="border-b border-border pb-4">
                        <p className="mt-4 max-w-4xl text-sm text-gray-600">
                            This view mirrors OTB row structure, but applies buy-month logic so
                            purchasing can review supplier-ready SKU rows, order quantities,
                            deposits, and planned arrivals for {selectedMonthLabel}.
                        </p>

                        <div className="mt-4 rounded-md border border-border bg-background p-3">
                            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                <div>
                                    <h3 className="text-sm font-semibold text-primary">
                                        Supplier schedule
                                    </h3>
                                    <p className="mt-1 text-xs text-gray-600">
                                        Export suppliers from here, then use the full row table below
                                        for SKU-by-SKU review in the same month. Supplier totals only
                                        reflect rows currently marked Include.
                                    </p>
                                </div>
                                <p className="text-xs text-gray-600">
                                    {supplierGroups.length} supplier
                                    {supplierGroups.length === 1 ? "" : "s"} in scope · earliest plan{" "}
                                    {formatUpcomingBuyDate(summary.earliestPlannedDate)}
                                </p>
                            </div>

                            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                                {supplierGroups.length > 0 ? (
                                    supplierGroups.map((group) => (
                                        <div
                                            key={group.supplier}
                                            className="min-w-[280px] rounded-md border border-border bg-white px-3 py-3"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-primary">
                                                        {group.supplier}
                                                    </p>
                                                    <p className="mt-1 text-xs text-gray-600">
                                                        {group.rowCount} SKU row
                                                        {group.rowCount === 1 ? "" : "s"} ·{" "}
                                                        {formatUpcomingBuyNumber(group.unitsToOrder)}{" "}
                                                        units to order
                                                    </p>
                                                </div>
                                                <p className="text-sm font-semibold text-primary">
                                                    {formatUpcomingBuyCurrency(group.orderValue)}
                                                </p>
                                            </div>
                                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                                <div>
                                                    <p className="uppercase tracking-[0.08em]">
                                                        Buy units
                                                    </p>
                                                    <p className="mt-1 text-sm font-medium text-primary">
                                                        {formatUpcomingBuyNumber(group.buyUnits)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="uppercase tracking-[0.08em]">
                                                        Deposit
                                                    </p>
                                                    <p className="mt-1 text-sm font-medium text-primary">
                                                        {formatUpcomingBuyCurrency(
                                                            group.depositLiability
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-600">
                                                <span>
                                                    First plan{" "}
                                                    {formatUpcomingBuyDate(group.earliestPlannedDate)}
                                                </span>
                                                <SupplierExportMenu
                                                    group={group}
                                                    selectedMonthLabel={selectedMonthLabel}
                                                />
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="w-full rounded-md border border-dashed border-border bg-white px-4 py-6 text-sm text-gray-600">
                                        No supplier schedule is in scope for the selected buy month.
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-7">
                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                    Selected month
                                </p>
                                <p className="mt-1 text-base font-semibold text-primary">
                                    {selectedMonthLabel}
                                </p>
                            </div>
                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                    Included rows
                                </p>
                                <p className="mt-1 text-lg font-semibold text-primary">
                                    {formatUpcomingBuyNumber(summary.rowCount)}
                                </p>
                            </div>
                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                    Excluded rows
                                </p>
                                <p className="mt-1 text-lg font-semibold text-primary">
                                    {formatUpcomingBuyNumber(excludedRowCount)}
                                </p>
                            </div>
                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                    Buy units
                                </p>
                                <p className="mt-1 text-lg font-semibold text-primary">
                                    {formatUpcomingBuyNumber(summary.buyUnits)}
                                </p>
                            </div>
                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                    Units to order
                                </p>
                                <p className="mt-1 text-lg font-semibold text-primary">
                                    {formatUpcomingBuyNumber(summary.totalUnits)}
                                </p>
                            </div>
                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                    Order value
                                </p>
                                <p className="mt-1 text-lg font-semibold text-primary">
                                    {formatUpcomingBuyCurrency(summary.orderValue)}
                                </p>
                            </div>
                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                    Deposit liability
                                </p>
                                <p className="mt-1 text-lg font-semibold text-primary">
                                    {formatUpcomingBuyCurrency(summary.depositLiability)}
                                </p>
                            </div>
                        </div>
                    </div>
                ) : null}

                {loading ? (
                    <div className="mt-4">
                        <LoaderSpinner width="40" height="40" border="4" colorSpinner="gray" />
                    </div>
                ) : (
                    <>
                        {visibleSelectedRows.length > 0 ? (
                            <div className="sticky top-[128px] z-20 mt-4 border-b border-neutral-800 bg-neutral-950 px-3 py-2 text-white">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-xs font-semibold tabular-nums">
                                        {selectedCount} selected
                                    </span>
                                    <div className="mx-1 h-4 w-px bg-white/20" />
                                    <div className="shrink-0">
                                        <ProductCatalogBulkFieldMenu
                                            key={`bulk-override-${selectedCount}`}
                                            triggerLabel="Buy Override"
                                            menuTitle="Set Buy Override"
                                            options={[
                                                { label: "Include", value: "include" },
                                                { label: "Exclude", value: "exclude" },
                                            ]}
                                            menuWidthClassName="w-44"
                                            onSelect={(value) => {
                                                if (value !== "include" && value !== "exclude") return;
                                                const label = value === "include" ? "Include" : "Exclude";
                                                setPendingBulkOverride({ label, value });
                                                setBulkOverrideConfirmOpen(true);
                                            }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="inline-flex h-8 items-center rounded-md border border-neutral-800 bg-transparent px-2.5 text-xs text-white hover:bg-white/10"
                                        onClick={() => setSelectedRowIds([])}
                                    >
                                        Clear selection
                                    </button>
                                </div>
                            </div>
                        ) : null}

                        <div className="mt-4 overflow-hidden rounded-lg border border-border bg-white">
                            <div className="max-h-[calc(100vh-13rem)] overflow-auto thin-scrollbar">
                                <table className="min-w-full border-separate border-spacing-0 text-sm">
                                    <thead className="sticky top-0 z-20 bg-white">
                                        <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-600 shadow-[inset_0_-1px_0_0_var(--border)]">
                                            <th className="border-b border-border bg-white px-4 py-3 font-medium">
                                                <input
                                                    type="checkbox"
                                                    className="size-5 cursor-pointer rounded-md border-gray-300 accent-neutral-950 focus:ring-neutral-950"
                                                    aria-label="Select all buy rows"
                                                    checked={allVisibleSelected}
                                                    ref={(element) => {
                                                        if (element) {
                                                            element.indeterminate = someVisibleSelected;
                                                        }
                                                    }}
                                                    onChange={(event) =>
                                                        toggleSelectAllVisible(event.target.checked)
                                                    }
                                                />
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 font-medium min-w-[120px]">
                                                Supply SKU
                                            </th>
                                            <th className="w-[320px] min-w-[320px] border-b border-border bg-white px-5 py-3 font-medium">
                                                Product Name
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 font-medium">
                                                Category
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 font-medium">
                                                Sub Category
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 font-medium">
                                                Status
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 font-medium">
                                                Supplier
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 font-medium">
                                                Buy Override
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 text-right font-medium">
                                                Buy Units
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 text-right font-medium">
                                                Units To Order
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 text-right font-medium">
                                                Unit Cost
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 text-right font-medium">
                                                Order Value
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 text-right font-medium">
                                                Deposit
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 text-right font-medium">
                                                Lead Time Days
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 font-medium">
                                                PO Planned
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 font-medium">
                                                Arrival
                                            </th>
                                            <th className="border-b border-border bg-white px-5 py-3 font-medium">
                                                Notes
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td
                                                    className="px-5 py-8 text-center text-sm text-gray-600"
                                                    colSpan={17}
                                                >
                                                    <LoaderSpinner
                                                        width="40"
                                                        height="40"
                                                        border="4"
                                                        colorSpinner="gray"
                                                    />
                                                </td>
                                            </tr>
                                        ) : scopedRows.length > 0 ? (
                                            scopedRows.map((row) => {
                                                const override = getRowBuyOverride(row);
                                                const isExcluded = override === "exclude";
                                                const isSelected = selectedRowIds.includes(row.id);
                                                const mutedClass = isExcluded
                                                    ? "text-gray-500"
                                                    : "text-primary";

                                                return (
                                                    <tr
                                                        key={row.id}
                                                        className={`group align-top ${
                                                            isExcluded ? "bg-slate-50/70" : "bg-white"
                                                        }`}
                                                    >
                                                        <td className="border-b border-transparent px-4 py-3 group-hover:border-border">
                                                            <input
                                                                type="checkbox"
                                                                className="size-5 cursor-pointer rounded-md border-gray-300 accent-neutral-950 focus:ring-neutral-950"
                                                                aria-label={`Select ${row.supplySku}`}
                                                                checked={isSelected}
                                                                onChange={(event) =>
                                                                    toggleRowSelection(
                                                                        row.id,
                                                                        event.target.checked
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td className="border-b border-transparent px-5 py-3 group-hover:border-border min-w-[120px]">
                                                            <SupplySku sku={row.supplySku} />
                                                        </td>
                                                        <td className="w-[320px] min-w-[320px] border-b border-transparent px-5 py-3 group-hover:border-border">
                                                            <p
                                                                className={`whitespace-nowrap font-medium ${mutedClass}`}
                                                            >
                                                                {row.productName}
                                                            </p>
                                                            <p className="mt-1 text-xs text-gray-600">
                                                                {row.category}
                                                            </p>
                                                        </td>
                                                        <td
                                                            className={`border-b border-transparent px-5 py-3 group-hover:border-border ${mutedClass}`}
                                                        >
                                                            {row.category}
                                                        </td>
                                                        <td
                                                            className={`border-b border-transparent px-5 py-3 group-hover:border-border ${mutedClass}`}
                                                        >
                                                            {row.subCategory}
                                                        </td>
                                                        <td className="border-b border-transparent px-5 py-3 group-hover:border-border">
                                                            <span className={getUpcomingBuyStatusTone(row.status)}>
                                                                {getUpcomingBuyStatusLabel(row.status)}
                                                            </span>
                                                        </td>
                                                        <td className="border-b border-transparent px-5 py-3 group-hover:border-border">
                                                            <p
                                                                className={`min-w-[220px] font-medium ${mutedClass}`}
                                                            >
                                                                {row.supplier}
                                                            </p>
                                                        </td>
                                                        <td className="border-b border-transparent px-5 py-3 group-hover:border-border">
                                                            <span
                                                                className={`rounded-md px-2 py-1 text-[11px] font-medium uppercase shadow-none ${getBuyOverrideTone(
                                                                    override
                                                                )}`}
                                                            >
                                                                {override}
                                                            </span>
                                                        </td>
                                                        <td
                                                            className={`border-b border-transparent px-5 py-3 text-right font-medium group-hover:border-border ${mutedClass}`}
                                                        >
                                                            {formatUpcomingBuyNumber(row.buyUnits)}
                                                        </td>
                                                        <td
                                                            className={`border-b border-transparent px-5 py-3 text-right font-medium group-hover:border-border ${mutedClass}`}
                                                        >
                                                            {formatUpcomingBuyNumber(row.unitsToOrder)}
                                                        </td>
                                                        <td
                                                            className={`border-b border-transparent px-5 py-3 text-right font-medium group-hover:border-border ${mutedClass}`}
                                                        >
                                                            {formatUpcomingBuyCurrency(row.unitCost)}
                                                        </td>
                                                        <td
                                                            className={`border-b border-transparent px-5 py-3 text-right font-medium group-hover:border-border ${mutedClass}`}
                                                        >
                                                            {formatUpcomingBuyCurrency(row.orderValue)}
                                                        </td>
                                                        <td
                                                            className={`border-b border-transparent px-5 py-3 text-right font-medium group-hover:border-border ${mutedClass}`}
                                                        >
                                                            {formatUpcomingBuyCurrency(row.depositLiability)}
                                                        </td>
                                                        <td
                                                            className={`border-b border-transparent px-5 py-3 text-right font-medium group-hover:border-border ${mutedClass}`}
                                                        >
                                                            {formatUpcomingBuyNumber(row.leadTimeDays)}
                                                        </td>
                                                        <td className="border-b border-transparent px-5 py-3 group-hover:border-border">
                                                            <p className={`font-medium ${mutedClass}`}>
                                                                {formatUpcomingBuyDate(row.poPlannedDate)}
                                                            </p>
                                                        </td>
                                                        <td className="border-b border-transparent px-5 py-3 group-hover:border-border">
                                                            <p className={`font-medium ${mutedClass}`}>
                                                                {formatUpcomingBuyDate(
                                                                    row.expectedArrivalDate
                                                                )}
                                                            </p>
                                                        </td>
                                                        <td className="border-b border-transparent px-5 py-3 group-hover:border-border">
                                                            <p
                                                                className={`max-w-[220px] text-sm ${mutedClass}`}
                                                            >
                                                                {row.notes || "—"}
                                                            </p>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td
                                                    className="px-5 py-8 text-sm text-gray-600"
                                                    colSpan={17}
                                                >
                                                    No buy rows are available for the selected Eadin buy
                                                    month.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                <ViewGovernanceDialog
                    open={governanceOpen}
                    onOpenChange={setGovernanceOpen}
                    viewId="upcoming-buys"
                    viewName="Upcoming Buys"
                />

                <ConfrimModal
                    isOpen={bulkOverrideConfirmOpen}
                    isLoading={bulkOverrideSaving}
                    title={
                        pendingBulkOverride
                            ? `Apply "${pendingBulkOverride.label}" to ${selectedCount} selected row${selectedCount === 1 ? "" : "s"}?`
                            : "Apply buy override?"
                    }
                    confirmButtonTitle="Yes"
                    cancelClick={() => {
                        if (bulkOverrideSaving) return;
                        setBulkOverrideConfirmOpen(false);
                        setPendingBulkOverride(null);
                    }}
                    submitClick={submitBulkOverride}
                    size="max-w-lg"
                />
            </div>
        </section>
    );
};

export default UpcomingBuysTable;
